import { InfiniteData } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { MouseEvent, useCallback } from 'react';

import { PagedResponse } from './types/GeneralTypes';

export function assertUnreachable(v: never): never {
  throw new Error('assertUnreachable invariant violated. Received: ' + v);
}

export function notEmptyArray<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

export function onlyUnique(value: unknown, index: number, array: unknown[]) {
  return array.indexOf(value) === index;
}

export const RO_DATE_FORMAT = 'DD.MM.YYYY';

export const RO_DATE_TIME_FORMAT = 'DD.MM.YYYY HH:mm';

export const SERVER_DATE_TIME_FORMAT = 'yyyy-MM-dd HH:mm:ss';

export const SERVER_DATE_FORMAT = 'yyyy-MM-dd';

export const DAYJS_SERVER_DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

export const DAYJS_SERVER_DATE_FORMAT = 'YYYY-MM-DD';

export function usePreventPropagation(fnc: (() => unknown) | undefined) {
  return useCallback(
    (ev: MouseEvent<HTMLButtonElement>) => {
      ev.preventDefault();
      ev.stopPropagation();
      if (fnc) {
        fnc();
      }
    },
    [fnc],
  );
}

export const EMPTY_LIST = [];

export const EMPTY_OBJECT = {};

export const noOp = () => {
  /* no-op */
};

export function unique<T>(arr: T[] | undefined, keyExtractor: (it: T) => string | number) {
  if (arr == null) return EMPTY_LIST;
  const map: Record<string | number, T> = {};

  arr.forEach(it => {
    const key = keyExtractor(it);
    if (map[key] == null) {
      map[key] = it;
    }
  });

  return Object.getOwnPropertyNames(map).map(key => map[key]);
}

export function range(size: number, startAt = 0): number[] {
  return [...Array(size).keys()].map(i => i + startAt);
}

export const flattenInfiniteData = <T>(data: InfiniteData<PagedResponse<T>> | null | undefined) =>
  (data?.pages ?? []).map(page => page.list).reduce((acc, prev) => [...acc, ...prev], []);

export function wait(ms: number) {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}

export function getData<T>(response: AxiosResponse<T>) {
  return response.data;
}

export function formatDecimal(num: number | null | undefined) {
  if (num == null) return null;

  return (Math.round(num * 100) / 100).toFixed(2);
}

export * from './hooks';
export * from './components';
export * from './fileDownload';
export * from './hooks/useDerivePageTitle';
export * from './date/dateUtils';
export * from './types/GeneralTypes';
export * from './queryUtils';
export * from './form';

// TODO: remove this function in the future
export function dangerouslyConvertType<T, V = unknown>(input: V): T {
  return input as unknown as T;
}
